/**
 * useSubscription — fetch and cache the current user's payments-infra subscription.
 *
 * Returns tier, status, limits, usage, and portal/billing helpers.
 * Fetches once on mount when userId is available; re-fetches on userId change.
 */
import { useState, useEffect, useCallback } from 'react'
import { getSubscription, openBillingPortal, SubscriptionResult } from '@/lib/payments'

interface UseSubscriptionReturn {
  subscription:    SubscriptionResult | null
  loading:         boolean
  error:           string | null
  tier:            'free' | 'pro' | 'enterprise'
  isPro:           boolean
  isEnterprise:    boolean
  refetch:         () => void
  openPortal:      () => Promise<void>
  portalLoading:   boolean
}

/**
 * Testing-only tier override. Set REACT_APP_FORCE_TIER=free|pro|enterprise
 * (in .env.testing, .env.staging, or a gitignored .env.local) to bypass the
 * real payments-infra call and force every FEATURE_KEYS gate app-wide — one
 * env var flips Studio, Album, and Settings' gating all at once instead of
 * needing a real Stripe test subscription per tier per tester.
 *
 * Hard-gated on NODE_ENV !== 'production' so it can never take effect in a
 * real prod build (CRA always sets NODE_ENV=production for `yarn build`,
 * regardless of which .env file is loaded), even if the var leaked into
 * .env.prod by mistake.
 *
 * Does NOT fabricate the underlying `subscription` object (renewal date,
 * portal link, etc.) — BillingSection/Subscription.tsx will still show "no
 * subscription" while every other gate in the app reads as the forced tier.
 * That's intentional: this unblocks testing feature-gated UI, not the
 * billing management screens themselves.
 */
const FORCE_TIER_ENV = process.env.REACT_APP_FORCE_TIER
const FORCED_TIER: 'free' | 'pro' | 'enterprise' | null =
  process.env.NODE_ENV !== 'production' &&
  (FORCE_TIER_ENV === 'free' || FORCE_TIER_ENV === 'pro' || FORCE_TIER_ENV === 'enterprise')
    ? FORCE_TIER_ENV
    : null

export function useSubscription(userId: string | undefined): UseSubscriptionReturn {
  const [subscription, setSubscription] = useState<SubscriptionResult | null>(null)
  const [loading, setLoading]           = useState(false)
  const [error, setError]               = useState<string | null>(null)
  const [portalLoading, setPortalLoading] = useState(false)

  const fetchSubscription = useCallback(async () => {
    if (FORCED_TIER) return // skip the real network call entirely — tier is hardcoded below
    if (!userId) return
    setLoading(true)
    setError(null)
    try {
      const result = await getSubscription(userId)
      setSubscription(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load subscription')
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    fetchSubscription()
  }, [fetchSubscription])

  const openPortal = useCallback(async () => {
    if (!userId) return
    setPortalLoading(true)
    try {
      await openBillingPortal(userId)
    } catch (err) {
      console.error('[useSubscription] portal error:', err)
    } finally {
      setPortalLoading(false)
    }
  }, [userId])

  const tier = FORCED_TIER ?? subscription?.tier ?? 'free'

  return {
    subscription,
    loading:       FORCED_TIER ? false : loading,
    error,
    tier,
    isPro:         tier === 'pro' || tier === 'enterprise',
    isEnterprise:  tier === 'enterprise',
    refetch:       fetchSubscription,
    openPortal,
    portalLoading,
  }
}
